import { msg } from "@lingui/core/macro";
import { useLocation } from "@tanstack/solid-router";
import { createSignal, type JSX, Show } from "solid-js";
import { UserPlus } from "lucide-solid";
import { Button } from "@/components/ui/button";
import { TextField } from "@/components/ui/text-field";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { safeRedirectTarget, useSession } from "@/lib/auth";
import { Link } from "@/lib/u-router";
import type { ActiveContext } from "@/features/pos/hooks/use-active-context";

interface RestaurantGateProps {
  ctx: ActiveContext;
  children: (ctx: ActiveContext) => JSX.Element;
}

/**
 * Asegura que exista un restaurante (org) + sucursal antes de operar. Si no
 * existe, muestra el formulario de alta. El alta usa la creación atómica
 * server-side (CC-3) vía `bootstrapRestaurant`.
 *
 * Dos reglas anti-"botón pegado" (bug del onboarding):
 *
 *  1. **Invitado**: la creación de restaurante es server-side y exige sesión
 *     (`AuthUser` en `create_org_handler`) — un visitante SIEMPRE recibiría
 *     401. Presentarle el formulario era ofrecer un botón que jamás puede
 *     funcionar (el 401 moría en silencio y el poll local esperaba 30s para
 *     nada). En su lugar se muestra el CTA de crear cuenta / iniciar sesión,
 *     con `redirect` de vuelta a esta pantalla.
 *
 *  2. **Errores visibles**: `bootstrapRestaurant` lanza ante error HTTP o
 *     timeout de sincronización; aquí se captura y se pinta feedback inline
 *     (patrón CC-8: jamás onError→idle sin señal al usuario).
 */
export function RestaurantGate(props: RestaurantGateProps) {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const session = useSession();
  const location = useLocation();
  const [name, setName] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  // El botón solo puede crear cuando la BD local está lista (si no, el alta no
  // puede completarse) y hay un nombre. Deshabilitado mientras carga/crea.
  const canSubmit = () => Boolean(db()) && name().trim().length > 0 && !busy();

  async function create(e: Event) {
    e.preventDefault();
    if (!canSubmit()) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await props.ctx.bootstrapRestaurant(name());
    } catch (err) {
      console.error("[RestaurantGate] bootstrap failed:", err);
      setError(
        t(
          msg`We couldn't create your restaurant. Check your connection and try again — if it keeps failing, sign out and back in.`,
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <Show
      when={props.ctx.isReady()}
      fallback={
        <div class="mx-auto max-w-md py-12" data-testid="restaurant-gate">
          <h2 class="mb-2 text-xl font-semibold">{t(msg`Set up your restaurant`)}</h2>
          <Show
            when={!session.isVisitor()}
            fallback={
              <div data-testid="restaurant-gate-guest">
                <p class="mb-4 text-sm text-muted-foreground">
                  {t(
                    msg`Your restaurant lives in your account so your whole team can work on it. Create a free account (or sign in) to set it up.`,
                  )}
                </p>
                <div class="flex flex-col gap-2 sm:flex-row">
                  <Link
                    to="/register"
                    search={{ redirect: safeRedirectTarget(location().pathname) }}
                    data-testid="restaurant-gate-register-cta"
                    class="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                  >
                    <UserPlus class="h-4 w-4" />
                    {t(msg`Create account`)}
                  </Link>
                  <Link
                    to="/login"
                    search={{ redirect: safeRedirectTarget(location().pathname) }}
                    data-testid="restaurant-gate-signin-cta"
                    class="inline-flex h-10 items-center justify-center rounded-md border border-input bg-background px-4 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground"
                  >
                    {t(msg`Sign in`)}
                  </Link>
                </div>
              </div>
            }
          >
            <p class="mb-4 text-sm text-muted-foreground">
              {t(
                msg`Create your restaurant to start taking orders. A default branch is added automatically.`,
              )}
            </p>
            <form class="flex gap-2" onSubmit={create}>
              <TextField
                data-testid="restaurant-name-input"
                placeholder={t(msg`Restaurant name`)}
                value={name()}
                onInput={(e) => setName(e.currentTarget.value)}
              />
              <Button data-testid="create-restaurant-btn" type="submit" disabled={!canSubmit()}>
                {busy() ? t(msg`Creating…`) : t(msg`Create`)}
              </Button>
            </form>
            <Show when={error()}>
              {(message) => (
                <p
                  role="alert"
                  data-testid="restaurant-gate-error"
                  class="mt-3 text-sm text-destructive"
                >
                  {message()}
                </p>
              )}
            </Show>
          </Show>
        </div>
      }
    >
      {props.children(props.ctx)}
    </Show>
  );
}
